import { KeyboardArrowDown } from '@mui/icons-material'
import { Box, Button, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, ListSubheader } from '@mui/material'
import Grid from '@mui/material/Unstable_Grid2'
import { ChangeEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { assingBrandOrLine, showVipContent } from '../../../../../../../redux/actions/users'
import { IRootState } from '../../../../../../../redux/types'
import CustomSwitchComponent from '../../../../../../shared/components/buttons/SwitchCustom'
import { IBrand, ILine, ROLES } from '../../../../models/user'
import {
  isClient,
  isCommercial,
  isCommercialOrClient,
  isCommercialOrClientOrTrainer,
  isManager,
  isTrainingManager
} from '../../../../utils/RolesHelper'
import { VipActions } from './VipActions'
import { VipStatusChip } from './VipStatusChip'

interface UserBrandsTableProps {
  brands: IBrand[]
  setTmpBrandLines: (value: any) => void
  handleInputChange: (inputName: string, value: any) => void
  openModal: () => void
}

export function UserBrandsList({ brands, setTmpBrandLines, handleInputChange, openModal }: UserBrandsTableProps) {
  const { t } = useTranslation(['global'])
  const dispatch = useDispatch()

  const { user: authUser } = useSelector((state: IRootState) => state.auth)
  const { userProfileSelected } = useSelector((state: IRootState) => state.user)
  const [lines, setLines] = useState<ILine[]>([])
  const [brandSelectedId, setBrandSelectedId] = useState<number>(0)

  useEffect(() => {
    if (brandSelectedId > 0) {
      const brand = brands.find(brand => brand.id === brandSelectedId)
      if (brand) setLines(brand.brandLines)
    }
  }, [brands])

  const showActions = (): boolean => {
    return (
      isCommercialOrClient(userProfileSelected.role_id) &&
      isManager(authUser.role_id) &&
      !isCommercialOrClientOrTrainer(authUser.role_id) &&
      userProfileSelected.id !== authUser.id
    )
  }

  const handleAssignBrandOrLine = (id: number, type: string) => (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(
      assingBrandOrLine({
        id,
        type,
        checked: event.target.checked,
        user_id: userProfileSelected.id
      })
    )
  }

  const handleOpen = (id: number, brandLines: any, mode: string) => () => {
    if (brandLines.length > 0) {
      let tmpBrandLines: any = []
      brandLines.map((line: any) => {
        let tmpBrandLine = userProfileSelected.brandlines.find((ln: any) => line.id === ln.id)
        if (tmpBrandLine !== undefined) tmpBrandLines.push(tmpBrandLine)
      })

      setTmpBrandLines(tmpBrandLines)
    } else setTmpBrandLines([])

    dispatch(
      showVipContent({
        id,
        mode,
        user_id: userProfileSelected.id
      })
    )

    handleInputChange('mode', mode)
    openModal()
  }

  const openBrandLinesPanel = (brand: IBrand, event: any) => {
    if (event.target.type === 'checkbox' || event.target.role === 'button') return

    if (lines.length > 0 && brandSelectedId === brand.id) {
      setLines([])
      setBrandSelectedId(0)
    } else {
      setLines(brand.brandLines)
      setBrandSelectedId(brand.id)
    }
  }

  const isBrandActive = (id: number): boolean => {
    return userProfileSelected.brandsIds.includes(id)
  }

  const isBrandLineActive = (id: number): boolean => {
    return userProfileSelected.brandlinesIds.includes(id)
  }

  return (
    <Grid container>
      <Grid md={6} xs={12}>
        <List
          id="brand-list"
          sx={{ bgcolor: 'background.paper' }}
          subheader={
            <ListSubheader
              component="div"
              sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around' }}
              id="brand-list-subheader"
            >
              <span>{t('brand')}</span>
              <span>{t('VIP')}</span>
            </ListSubheader>
          }
        >
          {brands.map((brand: IBrand) => (
            <ListItem
              onClick={e => openBrandLinesPanel(brand, e)}
              key={`brand-${brand.id}`}
              secondaryAction={
                <Box>
                  {isBrandActive(brand.id) && (
                    <>
                      <VipStatusChip data={brand} />
                      <VipActions data={brand} typeData={'brand'} />
                      {showActions() && (
                        <Button
                          onClick={handleOpen(brand.id, brand.brandLines, 'brand')}
                          component="label"
                          variant="contained"
                          size="small"
                        >
                          Asignar
                        </Button>
                      )}
                    </>
                  )}
                  <IconButton aria-label="delete">
                    <KeyboardArrowDown
                      sx={[
                        { mr: -1, transition: '0.2s' },
                        brand?.brandLines?.length === 0 && { opacity: 0 },
                        brandSelectedId === brand.id && { transform: 'rotate(-90deg)' }
                      ]}
                    />
                  </IconButton>
                </Box>
              }
              disablePadding
            >
              <ListItemButton dense selected={brandSelectedId === brand.id}>
                {!isClient(authUser.role_id) && userProfileSelected.id !== authUser.id && (
                  <ListItemIcon>
                    <CustomSwitchComponent
                      disabled={
                        (isTrainingManager(authUser.role_id) &&
                          (userProfileSelected.role_id === ROLES.manager || isCommercial(userProfileSelected.role_id))) ||
                        authUser.role_id > userProfileSelected.role_id
                      }
                      checked={isBrandActive(brand.id)}
                      onChange={handleAssignBrandOrLine(brand.id, 'brand')}
                    />
                  </ListItemIcon>
                )}
                <ListItemText id={`id-brand-${brand.id}`} primary={brand.name} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Grid>

      {lines.length > 0 && (
        <Grid md={6} xs={12}>
          <List
            id="line-list"
            sx={{ bgcolor: 'background.paper' }}
            subheader={
              <ListSubheader
                sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around' }}
                component="div"
                id="line-list-subheader"
              >
                <span>{t('line')}</span>
                <span>{t('VIP')}</span>
              </ListSubheader>
            }
          >
            {lines.map((line: ILine) => (
              <ListItem
                key={`line-${line.id}`}
                secondaryAction={
                  isBrandLineActive(line.id) && (
                    <>
                      <VipStatusChip data={line} />
                      <VipActions data={line} typeData={'line'} />
                      {showActions() && (
                        <Button
                          onClick={handleOpen(line.id, [], 'line')}
                          component="label"
                          variant="contained"
                          tabIndex={-1}
                          size={'small'}
                        >
                          Asignar
                        </Button>
                      )}
                    </>
                  )
                }
                disablePadding
              >
                <ListItemButton dense>
                  {!isClient(authUser.role_id) && userProfileSelected.id !== authUser.id && (
                    <ListItemIcon>
                      <CustomSwitchComponent
                        disabled={
                          (isTrainingManager(authUser.role_id) &&
                            (userProfileSelected.role_id === ROLES.manager || isCommercial(userProfileSelected.role_id))) ||
                          authUser.role_id > userProfileSelected.role_id
                        }
                        checked={isBrandLineActive(line.id)}
                        onChange={handleAssignBrandOrLine(line.id, 'line')}
                      />
                    </ListItemIcon>
                  )}
                  <ListItemText id={`id-line-${line.id}`} primary={line.name} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Grid>
      )}
    </Grid>
  )
}
